// L2 · Utilidades de resolución de línea de facturación por CLAVE NATURAL (sombra, sin render).
//
// Contexto: `lineas_facturacion` (L1) es la tabla derivada con ID estable que reemplazará al índice
// posicional de los arrays `meses_facturacion` / `detalle_pagos_hardware`. En L2 corre en SOMBRA:
// se resuelve `linea_id` por clave natural y se compara contra la resolución por posición actual, sin
// cambiar ninguna superficie. NADIE escribe esta tabla desde la app (es derivada por trigger en la base).
//
// La clave natural de una línea es (negocio_id, tipo, ano, mes, tramo). Estas funciones NO tocan la DB:
// reciben las líneas ya cargadas de un negocio y devuelven la coincidencia (o null).

// Normaliza a string comparable: número o texto → "03", "2026", etc. (mes/ano viven como texto en la
// tabla y en el array; el tramo puede venir número). null/undefined → "".
const norm = (v) => (v === null || v === undefined ? "" : String(v));

/**
 * Busca la línea de un negocio por su clave natural.
 * @param {Array<Object>} lineasDelNegocio - filas de lineas_facturacion de UN negocio.
 * @param {{tipo:string, ano:(string|number), mes:(string|number), tramo?:(string|number)}} clave
 * @returns {Object|null} la línea que matchea, o null si no existe / clave incompleta.
 */
export const resolverLinea = (lineasDelNegocio, clave) => {
  if (!Array.isArray(lineasDelNegocio) || !clave) return null;
  const { tipo, ano, mes } = clave;
  if (!tipo || !ano || !mes) return null;
  const tramo = clave.tramo === null || clave.tramo === undefined ? 1 : clave.tramo;
  const t = norm(tipo);
  const a = norm(ano);
  const m = norm(mes);
  const tr = norm(tramo);
  return (
    lineasDelNegocio.find(
      (l) => norm(l.tipo) === t && norm(l.ano) === a && norm(l.mes) === m && norm(l.tramo) === tr
    ) || null
  );
};

/**
 * Reproduce en JS la derivación de la clave natural de HARDWARE de la función SQL
 * `public.nc_lineas_derivadas` (migración 20260919_l1_lineas_facturacion.sql), para poder resolver
 * `linea_id` en sombra desde el mismo array `detalle_pagos_hardware` que ve el loader.
 *
 * ESPEJO EXACTO de la rama hardware de nc_lineas_derivadas (10.1 — misma regla, dos motores):
 *   · ano/mes salen de COALESCE(facturafecha, fecha_factura) del tramo (NO del mes de display de la
 *     grilla, que usa una cascada de estimaciones distinta). Tramo sin esa fecha → NO tiene línea (null).
 *   · tramo = row_number() OVER (PARTITION BY 'YYYY-MM' ORDER BY posición) entre los tramos DATABLES.
 * Cualquier cambio en nc_lineas_derivadas (hardware) debe replicarse aquí, y viceversa.
 *
 * @param {Array<Object>} detalle - array crudo detalle_pagos_hardware del negocio (orden = posición).
 * @param {number} entryIdx - índice 0-based del tramo dentro de `detalle` (== venta_ref_index).
 * @returns {{tipo:'hardware', ano:string, mes:string, tramo:number}|null}
 */
export const derivarClaveHardware = (detalle, entryIdx) => {
  if (!Array.isArray(detalle) || entryIdx < 0 || entryIdx >= detalle.length) return null;
  const fechaDe = (e) => e?.facturafecha || e?.fecha_factura || null;
  const objetivo = detalle[entryIdx];
  const fechaObj = fechaDe(objetivo);
  if (!fechaObj) return null; // tramo no datable → sin línea (igual que el WHERE d_fecha IS NOT NULL)
  const ano = String(fechaObj).slice(0, 4);
  const mes = String(fechaObj).slice(5, 7);
  const ym = `${ano}-${mes}`;
  // Tramo = orden dentro del mes entre los tramos datables, por posición (ORDER BY ord). Se recorre el
  // array en orden y se cuenta cuántos datables del mismo YYYY-MM hay hasta el objetivo inclusive.
  let tramo = 0;
  for (let i = 0; i < detalle.length; i++) {
    const f = fechaDe(detalle[i]);
    if (!f) continue;
    if (String(f).slice(0, 7) === ym) tramo += 1;
    if (i === entryIdx) break;
  }
  return { tipo: "hardware", ano, mes, tramo };
};
